/**
 * Directorio Valoriza — corporate people directory.
 *
 * Two modes:
 *  - Authenticated (default): renders via React Router at /directory, full data.
 *  - Public embed: render <Directory embedKey="..." /> inline. The page wrapper
 *    at /directory-embed handles loading it without sidebar.
 */
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { directoryApi } from '../utils/api';
import {
  Search, Users, Building2, MapPin, Briefcase, Sparkles,
  Mail, ChevronRight, X, Camera, Trash2, Loader, Filter,
  Award, Calendar, UserCircle2, Network, Phone, Smartphone,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

// ─── helpers ────────────────────────────────────────────────────────────────
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso + 'T12:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }); }
  catch { return iso; }
};

const seniorityLabel = (fechaAnt) => {
  if (!fechaAnt) return null;
  const start = new Date(fechaAnt + 'T12:00');
  if (Number.isNaN(start.getTime())) return null;
  const now = new Date();
  const months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (months < 12) return `${months} ${months === 1 ? 'mes' : 'meses'}`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  return rem === 0
    ? `${years} ${years === 1 ? 'año' : 'años'}`
    : `${years}a ${rem}m`;
};

// ─── Avatar ─────────────────────────────────────────────────────────────────
function Avatar({ person, size = 'md', embedMode }) {
  const [imgError, setImgError] = useState(false);
  const url = person.has_photo && !imgError ? directoryApi.photoUrl(person.id, person.photo_version) : null;
  const sizeClasses = {
    sm: 'h-10 w-10 text-sm',
    md: 'h-14 w-14 text-base',
    lg: 'h-20 w-20 text-2xl',
    xl: 'h-28 w-28 text-3xl',
  }[size] || 'h-14 w-14 text-base';

  if (url) {
    return (
      <img
        src={url}
        alt={person.full_name}
        onError={() => setImgError(true)}
        className={`${sizeClasses} rounded-2xl object-cover shadow-md ring-2 ring-white/40 dark:ring-slate-800/40`}
      />
    );
  }
  return (
    <div
      className={`${sizeClasses} rounded-2xl flex items-center justify-center font-bold text-white shadow-md ring-2 ring-white/40 dark:ring-slate-800/40 select-none`}
      style={{
        background: `linear-gradient(135deg, ${person.avatar_colour}, ${person.avatar_colour}aa)`,
      }}
    >
      {person.initials}
    </div>
  );
}

// ─── Stat tile ──────────────────────────────────────────────────────────────
function StatTile({ icon: Icon, label, value, accent }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl bg-white/70 dark:bg-slate-900/60 backdrop-blur-sm border border-white/40 dark:border-slate-700/40 p-4 shadow-sm"
      data-testid={`stat-${label.toLowerCase()}`}
    >
      <div className="absolute -right-4 -top-4 h-20 w-20 rounded-full opacity-10" style={{ background: accent }} />
      <div className="flex items-center gap-3 relative">
        <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-2xl font-bold font-heading leading-tight">{value}</div>
          <div className="text-xs text-muted-foreground">{label}</div>
        </div>
      </div>
    </div>
  );
}

// ─── Filter chip group ──────────────────────────────────────────────────────
function FilterChips({ label, options, value, onChange, max = 8 }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? options : options.slice(0, max);
  if (!options.length) return null;
  return (
    <div className="space-y-1.5">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {visible.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => onChange(active ? null : opt.value)}
              data-testid={`filter-${label.toLowerCase()}-${opt.value}`}
              className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                active
                  ? 'bg-brand text-white border-brand shadow-sm'
                  : 'bg-card hover:bg-muted border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              {opt.value} <span className="opacity-60">({opt.count})</span>
            </button>
          );
        })}
        {options.length > max && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs px-2 py-1 rounded-full text-brand hover:underline"
          >
            {expanded ? 'Mostrar menos' : `+${options.length - max} más`}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Detail drawer ──────────────────────────────────────────────────────────
function PersonDetail({ person, onClose, onSelect, embedMode, isMe, onPhotoChanged }) {
  const [org, setOrg] = useState(null);
  const [orgLoading, setOrgLoading] = useState(true);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');

  useEffect(() => {
    let alive = true;
    setOrg(null);
    setOrgLoading(true);
    const fetchFn = embedMode
      ? axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/directory/embed/org/${person.id}?key=${embedMode}`).then(r => r.data)
      : directoryApi.org(person.id).then(r => r.data);
    fetchFn
      .then(d => { if (alive) setOrg(d); })
      .catch(() => { if (alive) setOrg(null); })
      .finally(() => { if (alive) setOrgLoading(false); });
    return () => { alive = false; };
  }, [person.id, embedMode]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    setPhotoError('');
    try {
      await directoryApi.uploadPhoto(file);
      onPhotoChanged?.();
    } catch (err) {
      setPhotoError(err?.response?.data?.detail || 'No se pudo subir la imagen');
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Quitar tu foto de perfil?')) return;
    try { await directoryApi.deletePhoto(); onPhotoChanged?.(); }
    catch (err) { setPhotoError(err?.response?.data?.detail || 'No se pudo eliminar'); }
  };

  return (
    <motion.aside
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', stiffness: 280, damping: 32 }}
      className="fixed top-0 right-0 bottom-0 w-full sm:max-w-md md:max-w-lg bg-card border-l border-border shadow-2xl z-50 overflow-y-auto"
      data-testid="person-detail-drawer"
    >
      {/* Top hero */}
      <div className="relative px-6 pt-6 pb-8 bg-gradient-to-br from-emerald-500/15 via-sky-500/10 to-violet-500/15 dark:from-emerald-900/30 dark:via-sky-900/20 dark:to-violet-900/30">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 h-9 w-9 rounded-full bg-card/80 hover:bg-card backdrop-blur flex items-center justify-center"
          data-testid="close-person-detail"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex flex-col items-center text-center gap-3">
          <div className="relative">
            <Avatar person={person} size="xl" />
            {isMe && (
              <div className="absolute -bottom-1 -right-1 flex gap-1">
                <label className="h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center shadow-lg cursor-pointer hover:bg-brand/90" data-testid="upload-photo-btn">
                  {photoUploading ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleUpload} disabled={photoUploading} />
                </label>
                {person.has_photo && (
                  <button onClick={handleDelete} className="h-8 w-8 rounded-full bg-destructive text-white flex items-center justify-center shadow-lg hover:bg-destructive/90" data-testid="delete-photo-btn">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            )}
          </div>
          <div>
            <h2 className="text-xl font-bold font-heading">{person.full_name}</h2>
            <p className="text-sm text-muted-foreground mt-0.5">{person.puesto || 'Sin puesto'}</p>
          </div>
          {photoError && (
            <p className="text-xs text-destructive" data-testid="photo-upload-error">{photoError}</p>
          )}
          <a
            href={`mailto:${person.email}`}
            className="inline-flex items-center gap-2 mt-1 px-4 py-2 rounded-full bg-brand text-white text-sm font-medium hover:bg-brand/90 transition-colors shadow-sm"
            data-testid="contact-email-btn"
          >
            <Mail className="h-4 w-4" /> {person.email}
          </a>
          {(person.movil || person.telefono) && (
            <div className="flex flex-wrap gap-2 justify-center mt-1">
              {person.movil && (
                <a
                  href={`tel:${person.movil}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-card/80 text-foreground text-xs font-medium hover:bg-card transition-colors shadow-sm border border-border"
                  data-testid="contact-mobile-btn"
                >
                  <Smartphone className="h-3.5 w-3.5" /> {person.movil}
                </a>
              )}
              {person.telefono && person.telefono !== person.movil && (
                <a
                  href={`tel:${person.telefono}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-card/80 text-foreground text-xs font-medium hover:bg-card transition-colors shadow-sm border border-border"
                  data-testid="contact-phone-btn"
                >
                  <Phone className="h-3.5 w-3.5" /> {person.telefono}
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-6 space-y-5">
        {/* Quick facts grid */}
        <div className="grid grid-cols-2 gap-2.5">
          {person.ubicacion && <FactCard icon={MapPin} label="Ubicación (CB)" value={person.ubicacion} />}
          {person.fecha_inicio && <FactCard icon={Calendar} label="En Valoriza desde" value={fmtDate(person.fecha_inicio)} />}
          {person.fecha_fin && (
            <FactCard icon={Calendar} label="Fin de contrato" value={fmtDate(person.fecha_fin)} />
          )}
          {person.direct_reports_count > 0 && (
            <FactCard icon={Users} label="Equipo directo" value={`${person.direct_reports_count} personas`} />
          )}
        </div>

        {/* Mini Org chart */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5">
            <Network className="h-3.5 w-3.5" /> Estructura
          </h3>
          {orgLoading ? (
            <div className="flex justify-center py-6"><Loader className="h-4 w-4 animate-spin text-muted-foreground" /></div>
          ) : org ? (
            <OrgChart data={org} onSelect={onSelect} highlightId={person.id} />
          ) : (
            <div className="text-sm text-muted-foreground text-center py-3">Sin datos de jerarquía</div>
          )}
        </div>
      </div>
    </motion.aside>
  );
}

function FactCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-muted/40 dark:bg-slate-800/40 p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div className="text-sm font-medium mt-1 break-words">{value}</div>
    </div>
  );
}

// ─── Mini org chart ─────────────────────────────────────────────────────────
function OrgChart({ data, onSelect, highlightId }) {
  const { n2, n1, user, reports } = data;
  const OrgCard = ({ p, highlighted }) => (
    <button
      onClick={() => p && onSelect?.(p)}
      disabled={!p}
      className={`flex items-center gap-2 px-3 py-2 rounded-xl border transition-all w-full text-left ${
        highlighted
          ? 'bg-brand/10 border-brand/30 ring-2 ring-brand/20'
          : 'bg-card border-border hover:border-brand/40 hover:shadow-sm'
      } ${!p ? 'opacity-60 cursor-default' : 'cursor-pointer'}`}
    >
      {p ? (
        <>
          <Avatar person={p} size="sm" />
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{p.full_name}</div>
            <div className="text-xs text-muted-foreground truncate">{p.puesto || '—'}</div>
          </div>
        </>
      ) : (
        <span className="text-xs italic text-muted-foreground">—</span>
      )}
    </button>
  );
  return (
    <div className="space-y-1.5">
      {n2 && (
        <>
          <OrgCard p={n2} />
          <div className="ml-5 h-3 border-l border-border" />
        </>
      )}
      {n1 && (
        <>
          <OrgCard p={n1} />
          <div className="ml-5 h-3 border-l border-border" />
        </>
      )}
      <OrgCard p={user} highlighted={highlightId === user?.id} />
      {reports?.length > 0 && (
        <div className="ml-5 mt-1 space-y-1 border-l border-border pl-3">
          <div className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider pb-1">
            Equipo directo ({reports.length})
          </div>
          {reports.slice(0, 8).map((r) => (
            <OrgCard key={r.id} p={r} />
          ))}
          {reports.length > 8 && (
            <div className="text-xs text-muted-foreground pt-1">+ {reports.length - 8} más…</div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Person card (grid item) ────────────────────────────────────────────────
function PersonCard({ person, onClick, embedMode }) {
  return (
    <motion.button
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -3 }}
      transition={{ duration: 0.2 }}
      onClick={() => onClick(person)}
      data-testid={`person-card-${person.id}`}
      className="group relative w-full text-left rounded-2xl bg-card border border-border hover:border-brand/40 hover:shadow-lg transition-all p-4 overflow-hidden"
    >
      <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-0 group-hover:opacity-10 transition-opacity"
        style={{ background: person.avatar_colour }} />
      <div className="flex gap-3 items-start relative">
        <Avatar person={person} size="md" embedMode={embedMode} />
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate group-hover:text-brand transition-colors">{person.full_name}</div>
          <div className="text-xs text-muted-foreground truncate mt-0.5">{person.puesto || '—'}</div>
          {person.funcion && (
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mt-1 truncate">{person.funcion}</div>
          )}
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-brand group-hover:translate-x-0.5 transition-all flex-shrink-0 mt-1" />
      </div>
    </motion.button>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────
export default function Directory({ embedKey = null }) {
  const { user: me } = useAuth();
  const isEmbed = !!embedKey;

  const [q, setQ] = useState('');
  const [funcion, setFuncion] = useState(null);
  const [flexMode, setFlexMode] = useState(null);
  const [agrupacion, setAgrupacion] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ funciones: [], flex_modes: [], agrupaciones: [] });
  const [people, setPeople] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState(null);
  const [photoBust, setPhotoBust] = useState(0); // re-fetch when photo changes
  const [showMagicLink, setShowMagicLink] = useState(false); // for embed photo upload

  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const PAGE_SIZE = 120;

  const fetchAll = useCallback(async (queryParams, append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const path = isEmbed
        ? `${apiBase}/api/directory/embed/list?key=${embedKey}&${new URLSearchParams(queryParams).toString()}`
        : null;
      const listFn = isEmbed
        ? axios.get(path).then(r => r.data)
        : directoryApi.list(queryParams).then(r => r.data);
      const data = await listFn;
      setPeople(prev => append ? [...prev, ...(data.people || [])] : (data.people || []));
      setTotal(data.total || 0);
    } catch (e) {
      console.error('directory list error', e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [isEmbed, embedKey, apiBase]);

  // Initial stats + filters
  useEffect(() => {
    (async () => {
      try {
        if (isEmbed) {
          const [s, f] = await Promise.all([
            axios.get(`${apiBase}/api/directory/embed/stats?key=${embedKey}`).then(r => r.data),
            axios.get(`${apiBase}/api/directory/embed/filters?key=${embedKey}`).then(r => r.data),
          ]);
          setStats(s); setFilters(f);
        } else {
          const [s, f] = await Promise.all([directoryApi.stats(), directoryApi.filters()]);
          setStats(s.data); setFilters(f.data);
        }
      } catch (e) { console.error('directory init', e); }
    })();
  }, [isEmbed, embedKey, apiBase]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      const params = { limit: PAGE_SIZE, skip: 0 };
      if (q) params.q = q;
      if (funcion) params.funcion = funcion;
      if (flexMode) params.flex_office = flexMode;
      if (agrupacion && !isEmbed) params.agrupacion = agrupacion;
      fetchAll(params, false);
    }, 250);
    return () => clearTimeout(t);
  }, [q, funcion, flexMode, agrupacion, fetchAll, isEmbed, photoBust]);

  const loadMore = () => {
    const params = { limit: PAGE_SIZE, skip: people.length };
    if (q) params.q = q;
    if (funcion) params.funcion = funcion;
    if (flexMode) params.flex_office = flexMode;
    if (agrupacion && !isEmbed) params.agrupacion = agrupacion;
    fetchAll(params, true);
  };

  const resetFilters = () => { setQ(''); setFuncion(null); setFlexMode(null); setAgrupacion(null); };
  const anyFilter = !!q || !!funcion || !!flexMode || !!agrupacion;

  const handlePhotoChanged = async () => {
    // Bump bust + reload selected with new photo_version
    setPhotoBust(v => v + 1);
    if (selected) {
      try {
        const d = isEmbed
          ? await axios.get(`${apiBase}/api/directory/embed/person/${selected.id}?key=${embedKey}`).then(r => r.data)
          : await directoryApi.person(selected.id).then(r => r.data);
        setSelected(d);
      } catch (e) { /* keep previous */ }
    }
  };

  const handleSelect = async (p) => {
    try {
      const d = isEmbed
        ? await axios.get(`${apiBase}/api/directory/embed/person/${p.id}?key=${embedKey}`).then(r => r.data)
        : await directoryApi.person(p.id).then(r => r.data);
      setSelected(d);
    } catch (e) {
      setSelected(p); // fallback to lightweight
    }
  };

  return (
    <div className="space-y-5" data-testid="directory-page">
      {/* HERO */}
      <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-emerald-500 via-cyan-500 to-violet-600 text-white p-6 sm:p-8 shadow-xl">
        <div className="absolute top-0 right-0 h-48 w-48 rounded-full bg-white/10 blur-3xl -translate-y-12 translate-x-12" />
        <div className="absolute bottom-0 left-0 h-32 w-32 rounded-full bg-white/10 blur-2xl translate-y-8 -translate-x-8" />
        <div className="relative">
          <div className="flex items-center gap-2 text-white/80 text-xs uppercase tracking-widest font-medium">
            <Sparkles className="h-3.5 w-3.5" /> Directorio Valoriza
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-heading mt-2 leading-tight">
            Encuentra a cualquier persona <br className="hidden sm:block" />
            <span className="opacity-80">en segundos.</span>
          </h1>
          <p className="text-white/85 mt-3 text-sm sm:text-base max-w-xl">
            Busca por nombre, puesto o área. Contacta al instante. Conoce a tu equipo y a quien hace que las cosas pasen.
          </p>

          {/* Search bar */}
          <div className="mt-6 relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              data-testid="directory-search-input"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nombre, puesto, email, área…"
              className="pl-12 pr-12 h-14 text-base bg-white text-slate-900 placeholder:text-slate-400 border-0 shadow-lg rounded-2xl focus-visible:ring-2 focus-visible:ring-white/70"
            />
            {q && (
              <button
                onClick={() => setQ('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full hover:bg-slate-100 text-slate-500 flex items-center justify-center"
                data-testid="clear-search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* STATS */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-2 gap-3">
          <StatTile icon={Users} label="Personal con identidad digital" value={stats.total_people} accent="#10b981" />
          <StatTile icon={Building2} label="Ubicaciones (CB)" value={stats.total_ubicaciones} accent="#f59e0b" />
        </div>
      )}

      {/* Prominent "upload your photo" banner — always visible */}
      <button
        onClick={() => setShowMagicLink(true)}
        data-testid="upload-photo-banner"
        className="group relative w-full overflow-hidden rounded-2xl border border-emerald-200 dark:border-emerald-900/40 bg-gradient-to-r from-emerald-50 via-sky-50 to-violet-50 dark:from-emerald-900/20 dark:via-sky-900/15 dark:to-violet-900/20 p-4 sm:p-5 text-left hover:shadow-lg transition-all"
      >
        <div className="absolute -right-6 -bottom-6 h-28 w-28 rounded-full bg-emerald-300/30 dark:bg-emerald-600/20 blur-2xl" />
        <div className="relative flex items-center gap-4">
          <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-sky-500 text-white flex items-center justify-center shadow-md shrink-0">
            <Camera className="h-6 w-6 sm:h-7 sm:w-7" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold font-heading text-base sm:text-lg leading-tight">
              ¿Eres tú? <span className="text-brand">Sube tu foto al directorio</span>
            </div>
            <div className="text-xs sm:text-sm text-muted-foreground mt-0.5">
              Te enviamos un enlace seguro a tu correo corporativo. Tarda menos de 30 segundos.
            </div>
          </div>
          <span className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand text-white text-sm font-medium shadow-sm group-hover:bg-brand/90 transition-colors">
            Subir foto <ChevronRight className="h-4 w-4" />
          </span>
          <ChevronRight className="sm:hidden h-5 w-5 text-brand shrink-0" />
        </div>
      </button>

      {/* FILTERS toggle (mobile) + actions */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          onClick={() => setShowFilters(v => !v)}
          className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border border-border hover:bg-muted transition-colors"
          data-testid="toggle-filters"
        >
          <Filter className="h-3.5 w-3.5" />
          {showFilters ? 'Ocultar filtros' : 'Filtros'}
        </button>
        <div className="text-xs text-muted-foreground">
          {loading
            ? 'Buscando…'
            : `${people.length} de ${total} resultados${anyFilter ? '' : ` · sobre ${stats?.total_people || 0} personas activas`}`}
          {anyFilter && (
            <button onClick={resetFilters} className="ml-2 text-brand hover:underline" data-testid="reset-filters">
              Limpiar
            </button>
          )}
        </div>
      </div>

      {/* FILTER CHIPS */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="rounded-2xl bg-muted/40 dark:bg-slate-900/40 p-4 space-y-4">
              <FilterChips label="Área funcional" options={filters.funciones} value={funcion} onChange={setFuncion} />
              <FilterChips label="Modalidad oficina" options={filters.flex_modes} value={flexMode} onChange={setFlexMode} />
              {!isEmbed && <FilterChips label="Agrupación" options={filters.agrupaciones} value={agrupacion} onChange={setAgrupacion} max={12} />}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* RESULTS GRID */}
      {loading && people.length === 0 ? (
        <div className="flex justify-center py-12"><Loader className="h-6 w-6 animate-spin text-brand" /></div>
      ) : people.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <UserCircle2 className="h-10 w-10 mx-auto mb-3 opacity-40" />
          No se encontraron personas con esos criterios.
        </div>
      ) : (
        <>
          <motion.div
            layout
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
            data-testid="directory-results-grid"
          >
            {people.map((p) => (
              <PersonCard key={p.id} person={p} onClick={handleSelect} embedMode={embedKey} />
            ))}
          </motion.div>
          {people.length < total && (
            <div className="flex justify-center pt-4">
              <Button
                onClick={loadMore}
                disabled={loadingMore}
                variant="outline"
                data-testid="load-more-btn"
                className="rounded-full"
              >
                {loadingMore
                  ? <><Loader className="h-4 w-4 animate-spin mr-1.5" /> Cargando…</>
                  : `Cargar más (${total - people.length} restantes)`}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Overlay + drawer */}
      <AnimatePresence>
        {selected && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelected(null)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
            />
            <PersonDetail
              person={selected}
              onClose={() => setSelected(null)}
              onSelect={handleSelect}
              embedMode={embedKey}
              isMe={!isEmbed && me?.id === selected.id}
              onPhotoChanged={handlePhotoChanged}
            />
          </>
        )}
      </AnimatePresence>

      {/* Embed: magic link photo upload modal */}
      {showMagicLink && (
        <MagicLinkModal
          embedKey={embedKey}
          authenticatedUser={!isEmbed ? me : null}
          onClose={() => setShowMagicLink(false)}
          onUploaded={() => { setShowMagicLink(false); setPhotoBust(v => v + 1); }}
        />
      )}
    </div>
  );
}

// ─── Magic link request modal / direct upload (auth) ─────────────────────────
function MagicLinkModal({ embedKey, authenticatedUser, onClose, onUploaded }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  const isAuth = !!authenticatedUser;

  const onFile = (f) => {
    if (!f) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(f.type)) {
      setError('Solo JPG, PNG o WebP'); return;
    }
    if (f.size > 2_000_000) { setError('Máximo 2 MB'); return; }
    setError('');
    setFile(f);
    const reader = new FileReader();
    reader.onload = e => setPreview(e.target.result);
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true); setError('');
    try {
      if (isAuth) {
        if (!file) { setError('Selecciona una imagen'); setLoading(false); return; }
        await directoryApi.uploadPhoto(file);
        onUploaded?.();
      } else {
        await directoryApi.requestPhotoMagicLink(email.trim().toLowerCase(), embedKey);
        setSent(true);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo procesar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-card border border-border rounded-2xl shadow-2xl max-w-md w-full p-6"
        data-testid="magic-link-modal"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="text-lg font-bold font-heading flex items-center gap-2">
              <Camera className="h-5 w-5 text-brand" /> Sube tu foto
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {isAuth
                ? 'Aparecerá en tu ficha del directorio corporativo.'
                : 'Te enviaremos un enlace seguro a tu correo corporativo para que actualices tu foto.'}
            </p>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-muted flex items-center justify-center" data-testid="magic-link-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {sent ? (
          <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/15 border border-emerald-200 dark:border-emerald-900/30 p-4 text-sm" data-testid="magic-link-sent">
            <p className="font-semibold text-emerald-700 dark:text-emerald-300 flex items-center gap-2">
              <Mail className="h-4 w-4" /> Si el email existe, recibirás un enlace en breve.
            </p>
            <p className="text-xs text-muted-foreground mt-2">El enlace caduca en 60 minutos. Revisa también la carpeta de spam.</p>
            <Button onClick={onClose} className="w-full mt-3" variant="outline">Cerrar</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            {isAuth ? (
              <div className="flex flex-col items-center gap-3">
                {preview ? (
                  <img src={preview} alt="Vista previa" className="h-28 w-28 rounded-2xl object-cover shadow-lg" />
                ) : (
                  <div
                    className="h-28 w-28 rounded-2xl flex items-center justify-center font-bold text-white text-3xl shadow-lg"
                    style={{ background: 'linear-gradient(135deg,#0ea5e9,#0ea5e9aa)' }}
                  >
                    <Camera className="h-8 w-8 opacity-80" />
                  </div>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => onFile(e.target.files?.[0])}
                  className="hidden"
                  data-testid="magic-link-file"
                />
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="magic-link-pick" className="gap-2">
                  <Camera className="h-4 w-4" /> {file ? 'Cambiar foto' : 'Elegir foto'}
                </Button>
                {file && <p className="text-xs text-muted-foreground">{file.name} · {(file.size/1024).toFixed(0)} KB</p>}
                <p className="text-xs text-muted-foreground text-center">JPG, PNG o WebP · Máx 2 MB</p>
              </div>
            ) : (
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">
                  Tu email corporativo
                </label>
                <Input
                  type="email"
                  required
                  placeholder="nombre@valorizasm.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="magic-link-email-input"
                  autoFocus
                />
              </div>
            )}
            {error && (
              <div className="text-xs text-destructive flex items-start gap-2" data-testid="magic-link-error">
                <X className="h-3.5 w-3.5 mt-0.5" /> {error}
              </div>
            )}
            <Button type="submit" disabled={(isAuth ? !file : !email) || loading} className="w-full" data-testid="magic-link-submit">
              {loading
                ? <><Loader className="h-4 w-4 animate-spin mr-1.5" /> {isAuth ? 'Subiendo…' : 'Enviando…'}</>
                : (isAuth ? 'Guardar foto' : 'Enviarme el enlace')}
            </Button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
